import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import type {SeveridadAviso} from '@/components/page-notices'
import {Button} from '@/components/ui/button'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {useRederiveAssessment, useSgsst, CHAPTER_STANDARD_COUNTS} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {cn} from '@/lib/utils'

/** El mismo criterio del banner sin pintarlo; junto a él para que no diverjan. */
export function useRegimeDriftSeverity(businessId: string, frozen?: boolean): SeveridadAviso | null {
  const capabilities = useCapabilities(businessId)
  const {data: state} = useSgsst(businessId)
  const godReadOnly = useGodModeReadOnly()
  const drift = state?.assessment?.drift
  if (!drift || !capabilities.sgsst_operar || godReadOnly) return null
  if (frozen) return 'amber'
  return drift.suggested_chapter === 3 ? 'red' : 'amber'
}

/** Once the autoevaluación is registered (`frozen`) it is informational: the regime only
 * re-derives when the next year opens. */
export function RegimeDriftBanner({
  businessId,
  linkToSgsst,
  frozen,
}: {
  businessId: string
  linkToSgsst?: boolean
  frozen?: boolean
}) {
  const {t} = useTranslation()
  const capabilities = useCapabilities(businessId)
  const {data: state} = useSgsst(businessId)
  const godReadOnly = useGodModeReadOnly()
  const rederive = useRederiveAssessment(businessId)

  const drift = state?.assessment?.drift
  if (!drift || !capabilities.sgsst_operar || godReadOnly) return null

  if (frozen)
    return (
      <div className="notice-amber rounded-lg px-4 py-3" data-testid="drift-frozen">
        <p className="text-sm">
          {t('sgsst.driftFrozen', {
            count: drift.suggested_worker_count,
            chapter: drift.suggested_chapter,
            standards: CHAPTER_STANDARD_COUNTS[drift.suggested_chapter],
            next: (state?.assessment?.period_year ?? 0) + 1,
          })}
        </p>
      </div>
    )

  // Capítulo III (risk IV/V or >50 workers) carries immediate Decreto 1072 duties: red tier.
  const actionTier = drift.suggested_chapter === 3
  const nextYear = (state?.assessment?.period_year ?? 0) + 1

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 rounded-lg px-4 py-3',
        actionTier ? 'notice-red' : 'notice-amber'
      )}
      data-testid={actionTier ? 'drift-action' : 'drift-ordinary'}>
      <p className="text-sm">
        {t(actionTier ? 'sgsst.drift.action' : 'sgsst.drift.message', {
          count: drift.suggested_worker_count,
          chapter: drift.suggested_chapter,
          standards: CHAPTER_STANDARD_COUNTS[drift.suggested_chapter],
          next: nextYear,
        })}
      </p>
      {linkToSgsst ? (
        <Button asChild size="sm" variant="outline">
          <Link to={`/businesses/${businessId}/sgsst`}>{t('sgsst.drift.goToSgsst')}</Link>
        </Button>
      ) : (
        <Button
          size="sm"
          variant="outline"
          disabled={rederive.isPending}
          onClick={() => rederive.mutate(drift.suggested_worker_count)}>
          {t('sgsst.drift.rederive')}
        </Button>
      )}
    </div>
  )
}
